// Owned by task 7.1 of the dashboard-redesign spec (Requirement 2).
//
// `BuildingSelector` renders the top-of-dashboard scope switcher: a searchable
// dropdown listing every building the tenant owns, with "All buildings"
// (aggregate) pinned as the first option.
//
// The component is CONTROLLED — the parent owns the `BuildingScope` state and
// re-fetches every widget when it changes. This file additionally owns the
// two persistence helpers `readPersistedScope` and `writePersistedScope`.
// They mirror `frontend/src/help-docs-panel/persist.ts`: every localStorage
// access is guarded so quota-exceeded, disabled localStorage, private-mode
// SecurityError, or malformed JSON never crashes the app.
//
// The persistence key namespace is `ledgiventa.dashboard.scope.${sessionKey}`
// where `sessionKey` is a hashed identifier (SHA-256 of the Clerk `ownerId`,
// first 16 hex chars) sourced from `useHashedSessionKey`. The raw `ownerId`
// never lands in localStorage — Constitution Rule II.

import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import type { BuildingScope } from "./types";

// ============================================================================
// Persistence helpers (colocated per task 7.1 brief)
// ============================================================================

/**
 * localStorage key namespace for the persisted dashboard scope.
 *
 * @remarks
 * Full key is `ledgiventa.dashboard.scope.${sessionKey}`. `sessionKey` is a
 * hashed identifier (SHA-256 of `ownerId`, first 16 hex chars) so the raw
 * `ownerId` never lands in localStorage (Constitution Rule II).
 */
const STORAGE_KEY_PREFIX = "ledgiventa.dashboard.scope.";

/**
 * SSR-safe accessor for `window.localStorage`. Returns `null` when running
 * server-side, when `window.localStorage` is undefined, or when accessing
 * `localStorage` itself throws (some browsers throw `SecurityError` on
 * access, not just on read/write).
 */
function getLocalStorage(): Storage | null {
  try {
    if (typeof window === "undefined") {
      return null;
    }
    if (typeof window.localStorage === "undefined") {
      return null;
    }
    return window.localStorage;
  } catch {
    return null;
  }
}

/**
 * Type-guard for `BuildingScope`. Enforces the discriminated-union schema
 * from `types.ts` — any other shape is rejected, so an unknown-versioned or
 * hand-tampered blob in localStorage is treated as absent.
 */
function isValidScope(value: unknown): value is BuildingScope {
  if (value === null || typeof value !== "object") {
    return false;
  }
  const record = value as Record<string, unknown>;
  if (record.kind === "all") {
    return true;
  }
  if (
    record.kind === "single" &&
    typeof record.buildingId === "string" &&
    record.buildingId.length > 0
  ) {
    return true;
  }
  return false;
}

/**
 * Read the persisted dashboard scope for the given hashed session key.
 *
 * @param sessionKey - The hashed session key (typically 16-char lowercase
 *   hex). When `undefined`, no read is attempted and `null` is returned —
 *   this is the "session-only" signal used when the user is still signing in
 *   or the hash has not yet resolved.
 * @returns The parsed `BuildingScope` on success, or `null` when the key is
 *   missing, the JSON is malformed, the schema does not match, or
 *   localStorage is unavailable. Never throws.
 */
export function readPersistedScope(sessionKey?: string): BuildingScope | null {
  if (sessionKey === undefined) {
    return null;
  }
  const storage = getLocalStorage();
  if (storage === null) {
    return null;
  }
  try {
    const raw = storage.getItem(STORAGE_KEY_PREFIX + sessionKey);
    if (raw === null) {
      return null;
    }
    const parsed: unknown = JSON.parse(raw);
    if (!isValidScope(parsed)) {
      return null;
    }
    // Return a fresh object stripped of any unknown fields — this keeps
    // downstream consumers from accidentally reading extras that a future
    // schema might have written.
    if (parsed.kind === "all") {
      return { kind: "all" };
    }
    return { kind: "single", buildingId: parsed.buildingId };
  } catch {
    return null;
  }
}

/**
 * Persist the dashboard scope under the given hashed session key.
 *
 * @param sessionKey - The hashed session key. When `undefined` the write is
 *   skipped entirely — the caller is running in session-only mode.
 * @param scope - The scope to persist. Only the discriminated-union fields
 *   are serialised; any unknown properties on the caller-supplied object
 *   are silently dropped.
 *
 * Silently swallows `QuotaExceededError`, `SecurityError`, and any other
 * localStorage exception. Never throws.
 */
export function writePersistedScope(
  sessionKey: string | undefined,
  scope: BuildingScope,
): void {
  if (sessionKey === undefined) {
    return;
  }
  const storage = getLocalStorage();
  if (storage === null) {
    return;
  }
  // Rebuild a minimal payload so unknown properties from the caller never
  // land on disk.
  const payload: BuildingScope =
    scope.kind === "all"
      ? { kind: "all" }
      : { kind: "single", buildingId: scope.buildingId };
  try {
    storage.setItem(STORAGE_KEY_PREFIX + sessionKey, JSON.stringify(payload));
  } catch {
    // Silently swallow QuotaExceededError, SecurityError, and any other
    // localStorage failure. The dashboard MUST NEVER crash the app because
    // of a storage write failure.
  }
}

// ============================================================================
// Component
// ============================================================================

/**
 * Minimal building shape the selector renders. The selector never reads any
 * other fields on a `Building`, so callers can pass either a full domain
 * `Building` or a projected `{ id, name }` pair from a widget hook.
 */
export interface Building {
  id: string;
  name: string;
}

/** Props for the `BuildingSelector` component. */
export interface BuildingSelectorProps {
  /** The tenant's building list. "All buildings" is always pinned above these. */
  buildings: readonly Building[];
  /** Controlled selection — the parent owns the scope state. */
  value: BuildingScope;
  /** Fires when the user selects a new scope. */
  onChange: (scope: BuildingScope) => void;
  /**
   * Hashed session key from `useHashedSessionKey`. When `undefined`,
   * persistence is disabled entirely (session-only state).
   */
  sessionKey?: string;
}

/**
 * `scopesEqual` — Deep equality on the small `BuildingScope` union. Kept
 * private because it's only useful for the mount-time hydration guard.
 */
function scopesEqual(a: BuildingScope, b: BuildingScope): boolean {
  if (a.kind === "all" && b.kind === "all") return true;
  if (a.kind === "single" && b.kind === "single") {
    return a.buildingId === b.buildingId;
  }
  return false;
}

/**
 * `BuildingSelector` — Searchable dropdown that switches the dashboard's
 * `BuildingScope`. Renders "All buildings" first, then every building in
 * the `buildings` prop, filtered by an in-panel search input.
 *
 * On mount, if a valid persisted scope exists under
 * `ledgiventa.dashboard.scope.${sessionKey}`, the component calls `onChange`
 * with it so the parent's state matches the last-used scope. When no
 * persisted state exists (or when `sessionKey` is `undefined`), the parent
 * keeps whatever default it started with — typically `{ kind: "all" }`.
 *
 * Every user selection is written back to localStorage (when `sessionKey`
 * is defined) before `onChange` fires, so a hard refresh restores the same
 * scope even if the parent forgets to re-hydrate.
 */
export function BuildingSelector({
  buildings,
  value,
  onChange,
  sessionKey,
}: BuildingSelectorProps): JSX.Element {
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [searchTerm, setSearchTerm] = useState<string>("");

  const containerRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  // Latest-value refs so the mount-time hydration effect can access up-to-date
  // props without re-running (it MUST fire exactly once).
  const valueRef = useRef<BuildingScope>(value);
  const onChangeRef = useRef<(s: BuildingScope) => void>(onChange);
  const buildingsRef = useRef<readonly Building[]>(buildings);
  const sessionKeyRef = useRef<string | undefined>(sessionKey);
  valueRef.current = value;
  onChangeRef.current = onChange;
  buildingsRef.current = buildings;
  sessionKeyRef.current = sessionKey;

  // Mount-time hydration: restore the persisted scope exactly once. If the
  // persisted `single` building no longer exists in `buildings`, silently
  // skip — the parent's default (`{ kind: "all" }`) is a safe fallback.
  const hydratedRef = useRef<boolean>(false);
  useEffect(() => {
    if (hydratedRef.current) return;
    hydratedRef.current = true;

    const persisted = readPersistedScope(sessionKeyRef.current);
    if (persisted === null) return;

    if (
      persisted.kind === "single" &&
      !buildingsRef.current.some((b) => b.id === persisted.buildingId)
    ) {
      return;
    }

    if (scopesEqual(persisted, valueRef.current)) return;
    onChangeRef.current(persisted);
  }, []);

  // Close the dropdown on outside click.
  useEffect(() => {
    if (!isOpen) return undefined;
    const handleClickOutside = (event: MouseEvent): void => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
        setSearchTerm("");
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen]);

  // Auto-focus the search input when the panel opens so the user can start
  // typing immediately.
  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [isOpen]);

  const filteredBuildings = useMemo<readonly Building[]>(() => {
    const term = searchTerm.trim().toLowerCase();
    if (term.length === 0) return buildings;
    return buildings.filter((b) => b.name.toLowerCase().includes(term));
  }, [buildings, searchTerm]);

  const currentLabel = useMemo<string>(() => {
    if (value.kind === "all") return "All buildings";
    const found = buildings.find((b) => b.id === value.buildingId);
    return found ? found.name : "—";
  }, [value, buildings]);

  const handleSelect = useCallback(
    (scope: BuildingScope): void => {
      writePersistedScope(sessionKey, scope);
      onChange(scope);
      setIsOpen(false);
      setSearchTerm("");
    },
    [sessionKey, onChange],
  );

  const handleTriggerKeyDown = (
    e: React.KeyboardEvent<HTMLButtonElement>,
  ): void => {
    if (e.key === "ArrowDown" || e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      setIsOpen(true);
    }
  };

  const handleSearchKeyDown = (
    e: React.KeyboardEvent<HTMLInputElement>,
  ): void => {
    if (e.key === "Escape") {
      e.preventDefault();
      setIsOpen(false);
      setSearchTerm("");
    }
  };

  return (
    <div ref={containerRef} className="relative inline-block w-full max-w-xs">
      <button
        type="button"
        aria-label="Building scope"
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        onClick={() => setIsOpen((o) => !o)}
        onKeyDown={handleTriggerKeyDown}
        className="flex w-full items-center justify-between rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 shadow-sm hover:border-gray-400 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
      >
        <span className="truncate">{currentLabel}</span>
        <svg
          aria-hidden="true"
          className={`ml-2 h-4 w-4 shrink-0 text-gray-400 transition-transform ${
            isOpen ? "rotate-180" : ""
          }`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M19 9l-7 7-7-7"
          />
        </svg>
      </button>

      {isOpen && (
        <div className="absolute z-50 mt-1 w-full rounded-md border border-gray-300 bg-white shadow-xl">
          <div className="border-b border-gray-200 p-2">
            <input
              ref={searchInputRef}
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyDown={handleSearchKeyDown}
              placeholder="Search buildings…"
              aria-label="Search buildings"
              className="block w-full rounded-md border border-gray-300 px-2 py-1 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
            />
          </div>
          <ul
            role="listbox"
            aria-label="Building scope options"
            className="max-h-60 overflow-y-auto py-1"
          >
            <li
              role="option"
              aria-selected={value.kind === "all"}
              onClick={() => handleSelect({ kind: "all" })}
              className={`cursor-pointer px-3 py-2 text-sm font-medium ${
                value.kind === "all"
                  ? "bg-blue-50 text-blue-700"
                  : "text-gray-900 hover:bg-gray-100"
              }`}
            >
              All buildings
            </li>
            {filteredBuildings.length === 0 && searchTerm.trim().length > 0 && (
              <li className="px-3 py-2 text-sm text-gray-500">
                No buildings match “{searchTerm}”
              </li>
            )}
            {filteredBuildings.map((b) => {
              const selected =
                value.kind === "single" && value.buildingId === b.id;
              return (
                <li
                  key={b.id}
                  role="option"
                  aria-selected={selected}
                  onClick={() =>
                    handleSelect({ kind: "single", buildingId: b.id })
                  }
                  className={`cursor-pointer px-3 py-2 text-sm ${
                    selected
                      ? "bg-blue-50 text-blue-700"
                      : "text-gray-900 hover:bg-gray-100"
                  }`}
                >
                  {b.name}
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
}
